import { cloneDeep, noop } from 'lodash-es';
import React, { useMemo } from 'react';

import { useCollectionServices } from 'hooks';
import { services } from 'services/base';
import { AboutItem, AboutMainModuleItem, AboutSubModuleItem, TitleLevelOptions } from 'types';
import { deprecatedMethod, getAboutConfig } from 'utils';

export const isAboutSubModuleItem = (module: AboutItem): module is AboutSubModuleItem => {
  return module.type === 'subModule';
};

export const isAboutMainModuleItem = (module: AboutItem): module is AboutMainModuleItem => {
  return module.type === 'mainModule';
};

function isValidTitleLevelOption(titleLevel: string): titleLevel is TitleLevelOptions {
  const VALID_TITLE_LEVEL_OPTIONS: TitleLevelOptions[] = ['domain', 'application', 'simple', 'netline'];
  if (titleLevel === 'netline') {
    deprecatedMethod(noop, "config.main.about.titleLevel='netline'", "config.main.titleLevel='simple'");
  }
  return VALID_TITLE_LEVEL_OPTIONS.includes(titleLevel as TitleLevelOptions);
}

const structureModules = (moduleVersions: AboutItem[], activeModuleId: string) => {
  const moduleMap = new Map<string, AboutMainModuleItem>();
  const subModules: AboutSubModuleItem[] = [];
  moduleVersions.forEach((module) => {
    if (!module.type || (module.type !== 'subModule' && module.type !== 'mainModule')) {
      throw new Error(
        `Module type error, module type missing or bad, module.type= ${module.type} in ${JSON.stringify(module)}`,
      );
    }

    if (isAboutMainModuleItem(module)) {
      moduleMap.set(module.id, cloneDeep(module));
      return;
    }
    if (isAboutSubModuleItem(module)) {
      subModules.push(module);
    }
  });

  subModules.forEach((subModule) => {
    const parent = moduleMap.get(subModule.parentId!);
    if (!parent) {
      throw new Error(
        `Submodule ${subModule.name} has invalid parent id: ${subModule.parentId}, or parent module was not registered.`,
      );
    }
    if (isAboutMainModuleItem(parent)) {
      if (!parent.subModules) {
        parent.subModules = [];
      }
      parent.subModules.push(subModule);
    }
  });

  const finalModuleStructure: AboutMainModuleItem[] = [];
  moduleMap.forEach((module) => {
    if (module.id === activeModuleId) {
      finalModuleStructure.unshift(module);
      return;
    }
    finalModuleStructure.push(module);
  });

  return finalModuleStructure;
};

const getActiveModule = (activeModuleId: string, modules: AboutItem[]) => {
  const activeModules = modules.filter((module) => {
    return (isAboutSubModuleItem(module) && module.parentId === activeModuleId) || module.id === activeModuleId;
  });
  if (!activeModules.length) {
    throw new Error(`No module find with the id: ${activeModuleId}`);
  }
  return structureModules(activeModules, activeModuleId)[0];
};

const DEFAULT_TITLE_LEVEL: TitleLevelOptions = 'simple';
const DEFAULT_BASE_TITLE = 'NetLine';

const getTitleLevel = () => {
  const titleLevelFromConfig = getAboutConfig('titleLevel');
  if (!titleLevelFromConfig) {
    return DEFAULT_TITLE_LEVEL;
  }

  if (!isValidTitleLevelOption(titleLevelFromConfig)) {
    throw new Error(
      `titleLevel is incorrect in "about", it could be "netline", "domain or "application". Current titleLevel is: ${titleLevelFromConfig}.
          For further information please check Main Config documentation!`,
    );
  }

  return titleLevelFromConfig;
};

const getTitle = (
  aboutTitleLevel: TitleLevelOptions,
  activeModule: AboutMainModuleItem,
  domainClassName: string | null,
) => {
  const domainName = getAboutConfig('domainName');
  const baseTitle = getAboutConfig('baseTitle') ?? DEFAULT_BASE_TITLE;
  if (aboutTitleLevel === 'domain' && domainName) {
    return (
      <>
        {baseTitle}
        {domainClassName && <span className={domainClassName}>{domainName}</span>}
      </>
    );
  }

  if (aboutTitleLevel === 'application' && activeModule.title) {
    return activeModule.title;
  }

  return baseTitle;
};

const useAboutModules = (domainClassName: string | null) => {
  const activeModuleId = services.appFrame.about.getActiveModuleId();

  const moduleList: AboutItem[] = useCollectionServices(services.appFrame.about.modules);
  const structuredModuleVersions = useMemo(() => {
    return structureModules(moduleList, activeModuleId);
  }, [activeModuleId, moduleList]);
  const activeModule = getActiveModule(activeModuleId, moduleList);

  const aboutTitleLevel = getTitleLevel();
  const dialogTitle = getTitle(aboutTitleLevel, activeModule, domainClassName);

  return {
    activeModule: activeModule,
    dialogTitle: dialogTitle,
    airlineLogoUrl: getAboutConfig('airlineLogoUrl'),
    tailLogoUrl: getAboutConfig('tailLogoUrl'),
    licenseUrl: activeModule.licenseUrl ?? getAboutConfig('licenseUrl'),
    moduleList,
    structuredModuleVersions,
    activeModuleId,
  };
};

export default useAboutModules;
